import {
  BugOutlined, CheckCircleOutlined, ClockCircleOutlined, ExperimentOutlined,
  PlusOutlined, SafetyCertificateOutlined, StopOutlined, ThunderboltOutlined
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Button, Card, Checkbox, Col, Form, Input, InputNumber, Progress, Row, Select,
  Space, Statistic, Table, Tabs, Tag, Typography
} from "antd";
import { useMemo, useState } from "react";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatDateTime } from "../../shared/utils/formatters";
import {
  pilotValidationApi, type CreateFindingPayload, type ObservationPayload, type PilotFinding,
  type PilotFindingSeverity, type PilotFindingStatus, type PilotFindingType
} from "./pilot-validation-api";

const severityColor: Record<PilotFindingSeverity, string> = {
  CRITICAL: "red", HIGH: "orange", MEDIUM: "gold", LOW: "blue"
};
const statusColor: Record<PilotFindingStatus, string> = {
  OPEN: "red", IN_PROGRESS: "processing", RESOLVED: "gold", VERIFIED: "green"
};

function customerLabel(customer: { firstName: string | null; lastName: string | null; companyName: string | null }) {
  return customer.companyName ?? ([customer.firstName, customer.lastName].filter(Boolean).join(" ") || "—");
}

export function PilotValidationPage() {
  const { locale } = useAppConfig();
  const zh = locale === "zh";
  const queryClient = useQueryClient();
  const dashboard = useQuery({ queryKey: ["pilot-dashboard"], queryFn: pilotValidationApi.dashboard });
  const findings = useQuery({ queryKey: ["pilot-findings"], queryFn: pilotValidationApi.findings });
  const observations = useQuery({ queryKey: ["pilot-observations"], queryFn: pilotValidationApi.observations });
  const repairOrders = useQuery({ queryKey: ["pilot-repair-orders"], queryFn: pilotValidationApi.repairOrders });
  const [showFindingForm, setShowFindingForm] = useState(false);
  const [showObservationForm, setShowObservationForm] = useState(false);
  const [findingForm] = Form.useForm<CreateFindingPayload>();
  const [observationForm] = Form.useForm<ObservationPayload>();
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["pilot-dashboard"] }),
      queryClient.invalidateQueries({ queryKey: ["pilot-findings"] }),
      queryClient.invalidateQueries({ queryKey: ["pilot-observations"] }),
      queryClient.invalidateQueries({ queryKey: ["pilot-repair-orders"] })
    ]);
  };
  const createFinding = useMutation({
    mutationFn: pilotValidationApi.createFinding,
    onSuccess: async () => { findingForm.resetFields(); setShowFindingForm(false); await refresh(); }
  });
  const updateFinding = useMutation({
    mutationFn: ({ id, status }: { id: string; status: PilotFindingStatus }) => pilotValidationApi.updateFinding(id, status),
    onSuccess: refresh
  });
  const recordObservation = useMutation({
    mutationFn: pilotValidationApi.recordObservation,
    onSuccess: async () => { observationForm.resetFields(); setShowObservationForm(false); await refresh(); }
  });
  const data = dashboard.data;
  const roOptions = useMemo(() => (repairOrders.data ?? []).map(order => ({
    value: order.id,
    label: `${order.roNumber} · ${order.vehicle.year} ${order.vehicle.make} ${order.vehicle.model} · ${customerLabel(order.customer)}${order.pilotObservation ? " · ✓" : ""}`
  })), [repairOrders.data]);

  const findingTable = (type: PilotFindingType) => <FindingBoard
    zh={zh}
    data={(findings.data ?? []).filter(item => item.type === type)}
    loading={findings.isLoading}
    updating={updateFinding.isPending}
    onStatus={(id, status) => updateFinding.mutate({ id, status })}
  />;

  return <section className="pilot-validation-page">
    <div className="page-title business-page-title">
      <div>
        <Typography.Text type="secondary">AUTOFLOW PRO · RELEASE 1.5</Typography.Text>
        <Typography.Title level={2}>{zh ? "门店试点验证" : "Pilot Validation"}</Typography.Title>
        <Typography.Paragraph type="secondary">
          {zh ? "验证真实维修流程，记录摩擦，阻止范围扩张。" : "Validate the real repair workflow, capture friction, and prevent scope expansion."}
        </Typography.Paragraph>
      </div>
      <Tag color="orange" icon={<StopOutlined />}>{zh ? "RELEASE 2 已阻断" : "RELEASE 2 BLOCKED"}</Tag>
    </div>
    <Alert
      type="info"
      showIcon
      message={zh ? "Pilot Validation 进行中" : "Pilot Validation in progress"}
      description={zh ? "只有达到 30 个连续营业日、100 个真实维修事件、零 Critical Bug，并满足到店与无培训完成标准后，Release 2 才能启动。" : "Release 2 remains blocked until all operating-day, real-event, critical-bug, arrival, and unassisted-workflow criteria pass."}
    />
    <Row gutter={[16, 16]} className="pilot-metric-grid">
      <Col xs={24} sm={12} xl={6}><Card loading={dashboard.isLoading}><Statistic title={zh ? "连续营业日" : "Consecutive operating days"} value={data?.metrics.consecutiveOperatingDays ?? 0} suffix="/ 30" prefix={<ClockCircleOutlined />} /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={dashboard.isLoading}><Statistic title={zh ? "真实维修事件" : "Real repair events"} value={data?.metrics.realRepairEvents ?? 0} suffix="/ 100" prefix={<ExperimentOutlined />} /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={dashboard.isLoading}><Statistic title={zh ? "未关闭 Critical Bug" : "Open critical bugs"} value={data?.metrics.criticalOpen ?? 0} prefix={<BugOutlined />} valueStyle={{ color: data?.metrics.criticalOpen ? "#cf1322" : "#3f8600" }} /></Card></Col>
      <Col xs={24} sm={12} xl={6}><Card loading={dashboard.isLoading}><Statistic title={zh ? "60 秒内接车" : "Arrival within 60s"} value={data?.metrics.arrivalSuccessRate ?? 0} suffix="%" prefix={<ThunderboltOutlined />} /></Card></Col>
    </Row>
    <Card className="pilot-gate-card" title={zh ? "Release 2 启动门槛" : "Release 2 gate"}>
      <div className="pilot-criteria-grid">
        <Criterion passed={data?.criteria.consecutiveOperatingDays ?? false} label={zh ? "30 个连续营业日" : "30 consecutive operating days"} />
        <Criterion passed={data?.criteria.realRepairEvents ?? false} label={zh ? "100 个真实维修事件" : "100 real repair events"} />
        <Criterion passed={data?.criteria.noCriticalBugs ?? false} label={zh ? "没有 Critical Bug" : "No critical bugs"} />
        <Criterion passed={data?.criteria.customerArrival ?? false} label={zh ? "客户接车 ≤ 60 秒" : "Customer arrival ≤ 60 seconds"} />
        <Criterion passed={data?.criteria.unassistedCoreWorkflow ?? false} label={zh ? "无需培训完成核心流程" : "Core workflow completed without training"} />
      </div>
      <Progress percent={Math.round(Object.values(data?.criteria ?? {}).filter(Boolean).length / 5 * 100)} status="active" />
    </Card>
    {(createFinding.isError || updateFinding.isError || recordObservation.isError) &&
      <Alert type="error" showIcon message={String(createFinding.error ?? updateFinding.error ?? recordObservation.error)} />}
    <Tabs
      className="pilot-deliverable-tabs"
      items={[
        {
          key: "bugs",
          label: zh ? "Pilot Bug Board" : "Pilot Bug Board",
          children: <DeliverableCard
            title={zh ? "Pilot Bug Board" : "Pilot Bug Board"}
            action={<Button type="primary" icon={<PlusOutlined />} onClick={() => { findingForm.setFieldValue("type", "BUG"); setShowFindingForm(value => !value); }}>{zh ? "记录 Bug" : "Record bug"}</Button>}
          >
            {showFindingForm && <FindingForm zh={zh} form={findingForm} roOptions={roOptions} loading={createFinding.isPending} onFinish={payload => createFinding.mutate(payload)} />}
            {findingTable("BUG")}
          </DeliverableCard>
        },
        {
          key: "friction",
          label: zh ? "UX 摩擦日志" : "UX Friction Log",
          children: <DeliverableCard
            title={zh ? "UX 摩擦日志" : "UX Friction Log"}
            action={<Button type="primary" icon={<PlusOutlined />} onClick={() => { findingForm.setFieldValue("type", "UX_FRICTION"); setShowFindingForm(value => !value); }}>{zh ? "记录摩擦" : "Record friction"}</Button>}
          >
            {showFindingForm && <FindingForm zh={zh} form={findingForm} roOptions={roOptions} loading={createFinding.isPending} onFinish={payload => createFinding.mutate(payload)} />}
            {findingTable("UX_FRICTION")}
          </DeliverableCard>
        },
        {
          key: "workflow",
          label: zh ? "流程观察报告" : "Workflow Observation Report",
          children: <DeliverableCard
            title={zh ? "流程观察报告" : "Workflow Observation Report"}
            action={<Space>
              <Button icon={<PlusOutlined />} onClick={() => { findingForm.setFieldValue("type", "WORKFLOW_OBSERVATION"); setShowFindingForm(value => !value); }}>{zh ? "记录观察" : "Record observation"}</Button>
              <Button type="primary" icon={<SafetyCertificateOutlined />} onClick={() => setShowObservationForm(value => !value)}>{zh ? "登记真实维修事件" : "Admit real repair event"}</Button>
            </Space>}
          >
            {showFindingForm && <FindingForm zh={zh} form={findingForm} roOptions={roOptions} loading={createFinding.isPending} onFinish={payload => createFinding.mutate(payload)} />}
            {showObservationForm && <ObservationForm zh={zh} form={observationForm} roOptions={roOptions} loading={recordObservation.isPending} onFinish={payload => recordObservation.mutate(payload)} />}
            {findingTable("WORKFLOW_OBSERVATION")}
            <Typography.Title level={5}>{zh ? "已登记的真实维修事件" : "Admitted real repair events"}</Typography.Title>
            <Table
              rowKey="id"
              loading={observations.isLoading}
              dataSource={observations.data ?? []}
              scroll={{ x: 980 }}
              columns={[
                { title: "RO", key: "ro", render: (_, row) => <b>{row.repairOrder.roNumber}</b> },
                { title: zh ? "车辆" : "Vehicle", key: "vehicle", render: (_, row) => `${row.repairOrder.vehicle.year} ${row.repairOrder.vehicle.make} ${row.repairOrder.vehicle.model}` },
                { title: zh ? "接车秒数" : "Arrival seconds", dataIndex: "customerArrivalSeconds", key: "arrival", render: value => value ?? "—" },
                { title: zh ? "流程完成" : "Completed", dataIndex: "coreWorkflowCompleted", key: "complete", render: value => value ? <Tag color="green">YES</Tag> : <Tag>NO</Tag> },
                { title: zh ? "无需培训" : "Unassisted", dataIndex: "completedWithoutTraining", key: "training", render: value => value ? <Tag color="green">YES</Tag> : <Tag>NO</Tag> },
                { title: zh ? "观察时间" : "Observed", dataIndex: "observedAt", key: "time", render: formatDateTime }
              ]}
            />
          </DeliverableCard>
        },
        {
          key: "performance",
          label: zh ? "性能基准" : "Performance Benchmark",
          children: <PerformanceBenchmark zh={zh} dashboard={data} loading={dashboard.isLoading} />
        }
      ]}
    />
  </section>;
}

function Criterion({ passed, label }: { passed: boolean; label: string }) {
  return <div className={passed ? "pilot-criterion passed" : "pilot-criterion"}>
    {passed ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
    <span>{label}</span>
    <Tag color={passed ? "green" : "default"}>{passed ? "PASS" : "PENDING"}</Tag>
  </div>;
}

function DeliverableCard({ title, action, children }: { title: string; action: React.ReactNode; children: React.ReactNode }) {
  return <Card title={title} extra={action}>{children}</Card>;
}

function FindingForm({ zh, form, roOptions, loading, onFinish }: {
  zh: boolean;
  form: ReturnType<typeof Form.useForm<CreateFindingPayload>>[0];
  roOptions: Array<{ value: string; label: string }>;
  loading: boolean;
  onFinish: (payload: CreateFindingPayload) => void;
}) {
  return <Card size="small" className="pilot-entry-form">
    <Form form={form} layout="vertical" initialValues={{ type: "BUG", severity: "MEDIUM" }} onFinish={onFinish}>
      <Row gutter={12}>
        <Col xs={24} md={6}><Form.Item name="type" label={zh ? "类型" : "Type"} rules={[{ required: true }]}><Select options={[
          { value: "BUG", label: "BUG" }, { value: "UX_FRICTION", label: "UX FRICTION" }, { value: "WORKFLOW_OBSERVATION", label: "WORKFLOW OBSERVATION" }
        ]} /></Form.Item></Col>
        <Col xs={24} md={6}><Form.Item name="severity" label={zh ? "严重程度" : "Severity"} rules={[{ required: true }]}><Select options={[
          { value: "CRITICAL", label: "CRITICAL" }, { value: "HIGH", label: "HIGH" }, { value: "MEDIUM", label: "MEDIUM" }, { value: "LOW", label: "LOW" }
        ]} /></Form.Item></Col>
        <Col xs={24} md={6}><Form.Item name="workflowStep" label={zh ? "流程步骤" : "Workflow step"}><Input /></Form.Item></Col>
        <Col xs={24} md={6}><Form.Item name="observedRole" label={zh ? "观察角色" : "Observed role"}><Input /></Form.Item></Col>
      </Row>
      <Form.Item name="repairOrderId" label={zh ? "关联维修工单" : "Related repair order"}><Select allowClear showSearch optionFilterProp="label" options={roOptions} /></Form.Item>
      <Form.Item name="title" label={zh ? "标题" : "Title"} rules={[{ required: true }]}><Input maxLength={160} /></Form.Item>
      <Form.Item name="description" label={zh ? "事实与复现过程" : "Facts and reproduction"} rules={[{ required: true }]}><Input.TextArea rows={3} maxLength={4000} /></Form.Item>
      <div className="pilot-form-actions"><Button type="primary" htmlType="submit" loading={loading}>{zh ? "保存记录" : "Save record"}</Button></div>
    </Form>
  </Card>;
}

function ObservationForm({ zh, form, roOptions, loading, onFinish }: {
  zh: boolean;
  form: ReturnType<typeof Form.useForm<ObservationPayload>>[0];
  roOptions: Array<{ value: string; label: string }>;
  loading: boolean;
  onFinish: (payload: ObservationPayload) => void;
}) {
  return <Card size="small" className="pilot-entry-form">
    <Form form={form} layout="vertical" initialValues={{ coreWorkflowCompleted: false, completedWithoutTraining: false }} onFinish={onFinish}>
      <Form.Item name="repairOrderId" label={zh ? "真实维修工单" : "Real repair order"} rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={roOptions} /></Form.Item>
      <Row gutter={12}>
        <Col xs={24} sm={8}><Form.Item name="customerArrivalSeconds" label={zh ? "客户接车用时（秒）" : "Customer arrival (seconds)"}><InputNumber min={0} precision={0} /></Form.Item></Col>
        <Col xs={24} sm={8}><Form.Item name="pageLoadMs" label={zh ? "页面载入（ms）" : "Page load (ms)"}><InputNumber min={0} precision={0} /></Form.Item></Col>
        <Col xs={24} sm={8}><Form.Item name="apiResponseMs" label={zh ? "API 响应（ms）" : "API response (ms)"}><InputNumber min={0} precision={0} /></Form.Item></Col>
      </Row>
      <Space size="large" wrap>
        <Form.Item name="coreWorkflowCompleted" valuePropName="checked"><Checkbox>{zh ? "完整维修流程已完成" : "Core workflow completed"}</Checkbox></Form.Item>
        <Form.Item name="completedWithoutTraining" valuePropName="checked"><Checkbox>{zh ? "用户无需培训即可完成" : "Completed without training"}</Checkbox></Form.Item>
      </Space>
      <Form.Item name="notes" label={zh ? "现场观察备注" : "Shop-floor observation notes"}><Input.TextArea rows={3} maxLength={4000} /></Form.Item>
      <div className="pilot-form-actions"><Button type="primary" htmlType="submit" loading={loading}>{zh ? "计入 Pilot 指标" : "Admit to pilot metrics"}</Button></div>
    </Form>
  </Card>;
}

function FindingBoard({ zh, data, loading, updating, onStatus }: {
  zh: boolean;
  data: PilotFinding[];
  loading: boolean;
  updating: boolean;
  onStatus: (id: string, status: PilotFindingStatus) => void;
}) {
  return <Table
    rowKey="id"
    loading={loading}
    dataSource={data}
    scroll={{ x: 1050 }}
    columns={[
      { title: zh ? "严重程度" : "Severity", dataIndex: "severity", key: "severity", render: value => <Tag color={severityColor[value as PilotFindingSeverity]}>{value}</Tag> },
      { title: zh ? "问题" : "Finding", key: "finding", render: (_, row) => <div><b>{row.title}</b><Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }}>{row.description}</Typography.Paragraph></div> },
      { title: zh ? "流程 / 角色" : "Step / role", key: "context", render: (_, row) => <div>{row.workflowStep ?? "—"}<small className="table-secondary">{row.observedRole ?? "—"}</small></div> },
      { title: "RO", key: "ro", render: (_, row) => row.repairOrder?.roNumber ?? "—" },
      { title: zh ? "状态" : "Status", key: "status", width: 170, render: (_, row) => <Select
        value={row.status}
        disabled={updating}
        onChange={value => onStatus(row.id, value)}
        options={(["OPEN", "IN_PROGRESS", "RESOLVED", "VERIFIED"] as PilotFindingStatus[]).map(value => ({ value, label: value.replaceAll("_", " ") }))}
        labelRender={({ label, value }) => <Tag color={statusColor[value as PilotFindingStatus]}>{label}</Tag>}
      /> },
      { title: zh ? "观察时间" : "Observed", dataIndex: "observedAt", key: "time", render: formatDateTime }
    ]}
  />;
}

function PerformanceBenchmark({ zh, dashboard, loading }: {
  zh: boolean;
  dashboard: Awaited<ReturnType<typeof pilotValidationApi.dashboard>> | undefined;
  loading: boolean;
}) {
  const performance = dashboard?.performance;
  return <Card title={zh ? "性能基准" : "Performance Benchmark"} loading={loading}>
    <Alert type="info" showIcon message={zh ? "基准来源" : "Benchmark source"} description={zh ? "基准只统计已登记真实维修事件中的实测值，不使用 Demo 或 Seed 数据。" : "Benchmarks use measurements attached to admitted real repair events; demo and seed data are excluded."} />
    <Row gutter={[16, 16]} className="pilot-performance-grid">
      <Col xs={24} md={8}><Card size="small"><Statistic title={zh ? "客户接车中位数" : "Arrival median"} value={performance?.arrivalMedianSeconds ?? 0} suffix="s" /></Card></Col>
      <Col xs={24} md={8}><Card size="small"><Statistic title={zh ? "页面载入 P95" : "Page load P95"} value={performance?.pageLoadP95Ms ?? 0} suffix="ms" /></Card></Col>
      <Col xs={24} md={8}><Card size="small"><Statistic title={zh ? "API 响应 P95" : "API response P95"} value={performance?.apiP95Ms ?? 0} suffix="ms" /></Card></Col>
    </Row>
    <Table
      rowKey="metric"
      pagination={false}
      dataSource={[
        { metric: zh ? "客户接车" : "Customer arrival", p50: performance?.arrivalMedianSeconds, p95: performance?.arrivalP95Seconds, unit: "s", target: "≤ 60s" },
        { metric: zh ? "页面载入" : "Page load", p50: performance?.pageLoadMedianMs, p95: performance?.pageLoadP95Ms, unit: "ms", target: "Observe" },
        { metric: "API", p50: performance?.apiMedianMs, p95: performance?.apiP95Ms, unit: "ms", target: "Observe" }
      ]}
      columns={[
        { title: zh ? "指标" : "Metric", dataIndex: "metric", key: "metric" },
        { title: "P50", key: "p50", render: (_, row) => row.p50 === null || row.p50 === undefined ? "—" : `${row.p50}${row.unit}` },
        { title: "P95", key: "p95", render: (_, row) => row.p95 === null || row.p95 === undefined ? "—" : `${row.p95}${row.unit}` },
        { title: zh ? "门槛" : "Target", dataIndex: "target", key: "target" }
      ]}
    />
  </Card>;
}
